/**
 * FikoGPUThicknessAnalyzer
 *
 * Profesyonel GPU-accelerated wall thickness analizi
 * WebGPU (tercih) → WebGL2 (fallback) → CPU (son çare)
 *
 * Özellikler:
 * - WGSL compute shader ile paralel ray casting
 * - **BATCHED PROCESSING** - TDR timeout önlemek için vertex'leri batch'lere böler
 * - Multi-direction ray cone ile hassas ölçüm
 * - Real-time istatistik hesaplama
 * - Histogram analizi
 * - SSS faktör hesaplama
 *
 * Performans hedefi:
 * - 100K vertex: <500ms (batched)
 * - 1M vertex: <5s (batched)
 *
 * TDR (Timeout Detection and Recovery) Notları:
 * - Windows varsayılan TDR timeout: 2 saniye
 * - Her batch 4096 vertex işler
 * - Batch'ler arası GPU'ya nefes aldırılır
 */

import * as THREE from 'three';
import { MeshBVH, SAH } from 'three-mesh-bvh';

// BVH prototype patching is handled centrally by FikoRaycastBVH.patchPrototypes()
// Do NOT patch prototypes here to avoid duplicate/racing patches.

/**
 * GPU modu tipi
 */
export type GPUMode = 'WebGPU' | 'WebGL2' | 'CPU';

/**
 * Analiz sonuç istatistikleri
 */
export interface ThicknessStatistics {
    minThickness: number;
    maxThickness: number;
    avgThickness: number;
    medianThickness: number;
    stdDeviation: number;
    criticalPercentage: number;
    validVertexCount: number;
    totalVertexCount: number;
    histogram: { bin: number; count: number }[];
}

/**
 * Analiz konfigürasyonu
 */
export interface ThicknessAnalysisConfig {
    /** Maksimum ray mesafesi (mm) */
    maxDistance: number;

    /** Self-intersection epsilon */
    epsilon: number;

    /** Kritik kalınlık eşiği (mm) */
    criticalThickness: number;

    /** Çoklu ray modu (koni içinde ek ray'lar) */
    multiRayEnabled: boolean;

    /** Koni açısı (derece) - multiRay için */
    coneAngle: number;

    /** Koni içindeki ray sayısı */
    coneRayCount: number;

    /** Histogram bin sayısı */
    histogramBins: number;

    /** İlerleme callback'i */
    onProgress?: (progress: number) => void;
}

const DEFAULT_CONFIG: ThicknessAnalysisConfig = {
    maxDistance: 1000,
    epsilon: 0.001,
    criticalThickness: 1.0,
    multiRayEnabled: true,
    coneAngle: 30,
    coneRayCount: 4,
    histogramBins: 50,
    onProgress: undefined
};

/**
 * GPU Thickness Analyzer
 */
export class FikoGPUThicknessAnalyzer {
    private gpuMode: GPUMode = 'CPU';
    private gpuDevice: GPUDevice | null = null;
    private gpuDeviceLost: boolean = false;
    private gpuInitPromise: Promise<void> | null = null;
    private config: ThicknessAnalysisConfig;

    // Pre-allocated buffers (zero-allocation pattern)
    private readonly _tempVec3_1 = new THREE.Vector3();
    private readonly _tempVec3_2 = new THREE.Vector3();
    private readonly _tempVec3_3 = new THREE.Vector3();
    private readonly _tempVec3_4 = new THREE.Vector3();
    private readonly _tempMatrix3 = new THREE.Matrix3();
    private readonly _raycaster = new THREE.Raycaster();

    // Cached data
    private cachedBVH: MeshBVH | null = null;
    private cachedMesh: THREE.Mesh | null = null;

    constructor(config: Partial<ThicknessAnalysisConfig> = {}) {
        this.config = { ...DEFAULT_CONFIG, ...config };
        this._raycaster.firstHitOnly = true;

        // GPU init promise'i kaydet - analyze'da beklenecek
        this.gpuInitPromise = this.initializeGPU();
    }

    /**
     * GPU modunu initialize et
     *
     * ÖNEMLİ: WebGPU compute shader DEVRE DIŞI bırakıldı.
     *
     * Sebep: WebGPU compute shader her vertex için TÜM triangle'ları test ediyor
     * (O(n×m) karmaşıklık). Bu Windows TDR timeout'una (2 saniye) neden oluyor.
     * Batched processing bile çözüm değil çünkü shader inner loop değişmedi.
     *
     * Çözüm: CPU BVH (O(n×log(m))) + Fragment Shader post-processing
     * - CPU BVH ile thickness hesaplama (three-mesh-bvh kullanarak)
     * - Fragment shader ile smooth color mapping (bilateral blur)
     * - Flow analiz sisteminin kullandığı pattern
     *
     * Bu yaklaşım:
     * - GPU hang OLMAZ (compute shader yok)
     * - Profesyonel smooth rendering (post-processing)
     * - CPU hesaplaması paralel (worker'larla daha da hızlandırılabilir)
     */
    private async initializeGPU(): Promise<void> {
        // WebGPU compute shader devre dışı - TDR timeout sorunu
        // Fragment shader post-processing kullanılacak (GPU hang riski yok)
        this.gpuMode = 'CPU';
    }

    /**
     * Mesh'ler için kalınlık analizi yap
     */
    async analyze(
        meshes: THREE.Mesh[],
        config?: Partial<ThicknessAnalysisConfig>
    ): Promise<{
        thicknesses: Float32Array;
        sssFactors: Float32Array;
        statistics: ThicknessStatistics;
    }> {
        // GPU initialization'ı bekle
        if (this.gpuInitPromise) {
            await this.gpuInitPromise;
        }

        const cfg = { ...this.config, ...config };

        // Geometry birleştir
        const mergedData = this.mergeMeshes(meshes);
        if (!mergedData) {
            throw new Error('No valid geometry found');
        }

        const { positions, normals, indices, vertexCount, triangleCount } = mergedData;

        // BVH oluştur
        cfg.onProgress?.(5);
        const bvhMesh = this.createBVHMesh(positions, normals, indices);

        // GPU moduna göre hesaplama yap
        cfg.onProgress?.(10);
        let thicknesses: Float32Array;

        if (this.gpuMode === 'WebGPU' && this.gpuDevice) {
            try {
                thicknesses = await this.analyzeWebGPU(
                    positions, normals, indices,
                    vertexCount, triangleCount, cfg
                );
            } catch (error: any) {
                // WebGPU hatası - CPU'ya düş
                this.gpuMode = 'CPU';
                thicknesses = await this.analyzeCPU(
                    positions, normals, bvhMesh, vertexCount, cfg
                );
            }
        } else if (this.gpuMode === 'WebGL2') {
            thicknesses = await this.analyzeWebGL2(
                positions, normals, bvhMesh, vertexCount, cfg
            );
        } else {
            thicknesses = await this.analyzeCPU(
                positions, normals, bvhMesh, vertexCount, cfg
            );
        }

        // SSS faktörleri hesapla
        cfg.onProgress?.(90);
        const sssFactors = this.computeSSSFactors(thicknesses, cfg);

        // İstatistikleri hesapla
        cfg.onProgress?.(95);
        const statistics = this.computeStatistics(thicknesses, cfg);

        cfg.onProgress?.(100);

        // Temizlik
        bvhMesh.geometry.dispose();
        (bvhMesh.material as THREE.Material).dispose();

        return { thicknesses, sssFactors, statistics };
    }

    /**
     * Mesh'leri birleştir ve raw data çıkar
     */
    private mergeMeshes(meshes: THREE.Mesh[]): {
        positions: Float32Array;
        normals: Float32Array;
        indices: Uint32Array;
        vertexCount: number;
        triangleCount: number;
    } | null {
        let totalVertices = 0;
        let totalIndices = 0;

        // Boyut hesapla
        for (const mesh of meshes) {
            if (!mesh.geometry) continue;
            const posAttr = mesh.geometry.getAttribute('position');
            if (!posAttr) continue;
            totalVertices += posAttr.count;

            const indexAttr = mesh.geometry.getIndex();
            if (indexAttr) {
                totalIndices += indexAttr.count;
            } else {
                totalIndices += posAttr.count;
            }
        }

        if (totalVertices === 0) return null;

        const positions = new Float32Array(totalVertices * 3);
        const normals = new Float32Array(totalVertices * 3);
        const indices = new Uint32Array(totalIndices);

        let vertexOffset = 0;
        let indexOffset = 0;

        for (const mesh of meshes) {
            if (!mesh.geometry) continue;

            mesh.updateMatrixWorld(true);

            const posAttr = mesh.geometry.getAttribute('position');
            let normAttr = mesh.geometry.getAttribute('normal');
            const indexAttr = mesh.geometry.getIndex();

            if (!posAttr) continue;

            // Normaller yoksa hesapla
            if (!normAttr) {
                mesh.geometry.computeVertexNormals();
                normAttr = mesh.geometry.getAttribute('normal');
            }

            const normalMatrix = this._tempMatrix3.getNormalMatrix(mesh.matrixWorld);

            // Vertex'leri kopyala (world space'e çevir)
            for (let i = 0; i < posAttr.count; i++) {
                const v = this._tempVec3_1.fromBufferAttribute(posAttr, i);
                v.applyMatrix4(mesh.matrixWorld);

                positions[(vertexOffset + i) * 3] = v.x;
                positions[(vertexOffset + i) * 3 + 1] = v.y;
                positions[(vertexOffset + i) * 3 + 2] = v.z;

                if (normAttr) {
                    const n = this._tempVec3_2.fromBufferAttribute(normAttr, i);
                    n.applyMatrix3(normalMatrix).normalize();

                    normals[(vertexOffset + i) * 3] = n.x;
                    normals[(vertexOffset + i) * 3 + 1] = n.y;
                    normals[(vertexOffset + i) * 3 + 2] = n.z;
                }
            }

            // Index'leri kopyala
            if (indexAttr) {
                for (let i = 0; i < indexAttr.count; i++) {
                    indices[indexOffset + i] = indexAttr.getX(i) + vertexOffset;
                }
                indexOffset += indexAttr.count;
            } else {
                for (let i = 0; i < posAttr.count; i++) {
                    indices[indexOffset + i] = vertexOffset + i;
                }
                indexOffset += posAttr.count;
            }

            vertexOffset += posAttr.count;
        }

        return {
            positions,
            normals,
            indices,
            vertexCount: totalVertices,
            triangleCount: Math.floor(totalIndices / 3)
        };
    }

    /**
     * BVH mesh oluştur
     */
    private createBVHMesh(
        positions: Float32Array,
        normals: Float32Array,
        indices: Uint32Array
    ): THREE.Mesh {
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
        geometry.setIndex(new THREE.BufferAttribute(indices, 1));

        const bvh = new MeshBVH(geometry, { strategy: SAH });
        (geometry as any).boundsTree = bvh;

        const material = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide });
        const mesh = new THREE.Mesh(geometry, material);

        this.cachedBVH = bvh;
        this.cachedMesh = mesh;

        return mesh;
    }

    /**
     * WebGPU compute shader ile analiz - BATCHED PROCESSING
     *
     * TDR (Timeout Detection and Recovery) önlemek için:
     * - Vertex'ler küçük batch'lere bölünür (varsayılan 2048)
     * - Her batch arasında GPU'ya "nefes" aldırılır
     * - Triangle sayısı çok fazlaysa batch boyutu küçültülür
     */
    private async analyzeWebGPU(
        positions: Float32Array,
        normals: Float32Array,
        indices: Uint32Array,
        vertexCount: number,
        triangleCount: number,
        config: ThicknessAnalysisConfig
    ): Promise<Float32Array> {
        if (!this.gpuDevice) {
            throw new Error('GPU device not available');
        }

        const device = this.gpuDevice;

        // TDR'yi önlemek için batch boyutunu triangle sayısına göre ayarla
        // Daha fazla triangle = daha küçük batch (her vertex daha uzun sürer)
        let batchSize = 2048;
        if (triangleCount > 100000) batchSize = 512;
        else if (triangleCount > 50000) batchSize = 1024;
        else if (triangleCount > 20000) batchSize = 1536;

        const totalBatches = Math.ceil(vertexCount / batchSize);

        // Shader kodu - batch offset destekli
        const shaderCode = this.getWebGPUShaderCodeBatched(config);

        // Shader module
        const shaderModule = device.createShaderModule({
            code: shaderCode
        });

        // Buffer'ları oluştur
        const positionsBuffer = device.createBuffer({
            size: positions.byteLength,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
        });
        device.queue.writeBuffer(positionsBuffer, 0, positions);

        const normalsBuffer = device.createBuffer({
            size: normals.byteLength,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
        });
        device.queue.writeBuffer(normalsBuffer, 0, normals);

        const indicesBuffer = device.createBuffer({
            size: indices.byteLength,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
        });
        device.queue.writeBuffer(indicesBuffer, 0, indices);

        const thicknessesBuffer = device.createBuffer({
            size: vertexCount * 4, // Float32
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
        });

        // Uniform buffer (batch offset dinamik olarak güncellenecek)
        const uniformBuffer = device.createBuffer({
            size: 32, // 8 x f32
            usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        });

        // Pipeline
        const pipeline = device.createComputePipeline({
            layout: 'auto',
            compute: {
                module: shaderModule,
                entryPoint: 'main',
            }
        });

        // Bind group
        const bindGroup = device.createBindGroup({
            layout: pipeline.getBindGroupLayout(0),
            entries: [
                { binding: 0, resource: { buffer: positionsBuffer } },
                { binding: 1, resource: { buffer: normalsBuffer } },
                { binding: 2, resource: { buffer: indicesBuffer } },
                { binding: 3, resource: { buffer: thicknessesBuffer } },
                { binding: 4, resource: { buffer: uniformBuffer } }
            ]
        });

        // Sonuç buffer'ı
        const resultArray = new Float32Array(vertexCount);

        // BATCHED PROCESSING - Her batch ayrı dispatch
        const workgroupSize = 64; // Daha küçük workgroup = daha az TDR riski

        for (let batchIdx = 0; batchIdx < totalBatches; batchIdx++) {
            const batchStart = batchIdx * batchSize;
            const batchEnd = Math.min(batchStart + batchSize, vertexCount);
            const currentBatchSize = batchEnd - batchStart;

            // Uniform'ları güncelle (batch offset ile)
            const uniformData = new Float32Array([
                vertexCount,           // 0: toplam vertex sayısı
                triangleCount,         // 1: toplam triangle sayısı
                config.maxDistance,    // 2: max ray mesafesi
                config.epsilon,        // 3: self-intersection epsilon
                (config.coneAngle * Math.PI) / 180, // 4: koni açısı (radyan)
                config.coneRayCount,   // 5: koni ray sayısı
                batchStart,            // 6: batch başlangıç offset'i
                currentBatchSize       // 7: bu batch'teki vertex sayısı
            ]);
            device.queue.writeBuffer(uniformBuffer, 0, uniformData);

            // Bu batch için dispatch
            const numWorkgroups = Math.ceil(currentBatchSize / workgroupSize);

            const commandEncoder = device.createCommandEncoder();
            const computePass = commandEncoder.beginComputePass();
            computePass.setPipeline(pipeline);
            computePass.setBindGroup(0, bindGroup);
            computePass.dispatchWorkgroups(numWorkgroups);
            computePass.end();

            device.queue.submit([commandEncoder.finish()]);

            // GPU'nun bu batch'i bitirmesini bekle - TDR önleme
            await (device.queue as any).onSubmittedWorkDone();

            // İlerleme callback
            const progress = 10 + (batchIdx / totalBatches) * 80;
            config.onProgress?.(progress);

            // Her 4 batch'te bir kısa bekleme - GPU'ya nefes aldır
            if (batchIdx % 4 === 3 && batchIdx < totalBatches - 1) {
                await new Promise(resolve => setTimeout(resolve, 1));
            }
        }

        // Tüm sonuçları oku
        const readbackBuffer = device.createBuffer({
            size: vertexCount * 4,
            usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
        });

        const copyEncoder = device.createCommandEncoder();
        copyEncoder.copyBufferToBuffer(
            thicknessesBuffer, 0,
            readbackBuffer, 0,
            vertexCount * 4
        );
        device.queue.submit([copyEncoder.finish()]);

        await readbackBuffer.mapAsync(GPUMapMode.READ);
        const mappedRange = readbackBuffer.getMappedRange();
        resultArray.set(new Float32Array(mappedRange));
        readbackBuffer.unmap();

        // Cleanup
        positionsBuffer.destroy();
        normalsBuffer.destroy();
        indicesBuffer.destroy();
        thicknessesBuffer.destroy();
        uniformBuffer.destroy();
        readbackBuffer.destroy();

        return resultArray;
    }

    /**
     * Batched WebGPU shader kodu
     * batchOffset ve batchSize uniform'ları ile çalışır
     */
    private getWebGPUShaderCodeBatched(_config: ThicknessAnalysisConfig): string {
        return /* wgsl */`
struct Uniforms {
    vertexCount: u32,
    triangleCount: u32,
    maxDistance: f32,
    epsilon: f32,
    coneAngle: f32,
    coneRayCount: u32,
    batchOffset: u32,
    batchSize: u32
}

@group(0) @binding(0) var<storage, read> positions: array<f32>;
@group(0) @binding(1) var<storage, read> normals: array<f32>;
@group(0) @binding(2) var<storage, read> indices: array<u32>;
@group(0) @binding(3) var<storage, read_write> thicknesses: array<f32>;
@group(0) @binding(4) var<uniform> uniforms: Uniforms;

// Möller–Trumbore ray-triangle intersection
fn rayTriangleIntersect(
    origin: vec3f, dir: vec3f,
    v0: vec3f, v1: vec3f, v2: vec3f
) -> f32 {
    let edge1 = v1 - v0;
    let edge2 = v2 - v0;
    let h = cross(dir, edge2);
    let a = dot(edge1, h);

    if (abs(a) < 0.0001) { return -1.0; }

    let f = 1.0 / a;
    let s = origin - v0;
    let u = f * dot(s, h);
    if (u < 0.0 || u > 1.0) { return -1.0; }

    let q = cross(s, edge1);
    let v = f * dot(dir, q);
    if (v < 0.0 || u + v > 1.0) { return -1.0; }

    let t = f * dot(edge2, q);
    if (t > uniforms.epsilon) { return t; }

    return -1.0;
}

fn computeTangentBasis(normal: vec3f) -> mat3x3f {
    var tangent: vec3f;
    if (abs(normal.x) < 0.9) {
        tangent = normalize(cross(normal, vec3f(1.0, 0.0, 0.0)));
    } else {
        tangent = normalize(cross(normal, vec3f(0.0, 1.0, 0.0)));
    }
    let bitangent = cross(normal, tangent);
    return mat3x3f(tangent, bitangent, normal);
}

// Ray cast with early termination
fn castRay(origin: vec3f, dir: vec3f, vertexIdx: u32) -> f32 {
    var minDist = uniforms.maxDistance;

    // Early termination için minimum mesafe takibi
    for (var t = 0u; t < uniforms.triangleCount; t++) {
        let i0 = indices[t * 3u];
        let i1 = indices[t * 3u + 1u];
        let i2 = indices[t * 3u + 2u];

        // Self-intersection kontrolü
        if (i0 == vertexIdx || i1 == vertexIdx || i2 == vertexIdx) {
            continue;
        }

        let v0 = vec3f(positions[i0 * 3u], positions[i0 * 3u + 1u], positions[i0 * 3u + 2u]);
        let v1 = vec3f(positions[i1 * 3u], positions[i1 * 3u + 1u], positions[i1 * 3u + 2u]);
        let v2 = vec3f(positions[i2 * 3u], positions[i2 * 3u + 1u], positions[i2 * 3u + 2u]);

        let hit = rayTriangleIntersect(origin, dir, v0, v1, v2);
        if (hit > 0.0 && hit < minDist) {
            minDist = hit;

            // Early termination: çok ince (kritik) bölge bulduk
            if (minDist < 1.0) {
                return minDist;
            }
        }
    }

    return minDist;
}

@compute @workgroup_size(64)
fn main(@builtin(global_invocation_id) gid: vec3u) {
    // Batch içindeki local index
    let localIdx = gid.x;
    if (localIdx >= uniforms.batchSize) {
        return;
    }

    // Global vertex index
    let idx = uniforms.batchOffset + localIdx;
    if (idx >= uniforms.vertexCount) {
        return;
    }

    let pos = vec3f(
        positions[idx * 3u],
        positions[idx * 3u + 1u],
        positions[idx * 3u + 2u]
    );

    let normal = normalize(vec3f(
        normals[idx * 3u],
        normals[idx * 3u + 1u],
        normals[idx * 3u + 2u]
    ));

    let inwardDir = -normal;
    let origin = pos + inwardDir * uniforms.epsilon;

    var minThickness = uniforms.maxDistance;

    // Ana yön (normal'ın tersi)
    let mainDist = castRay(origin, inwardDir, idx);
    minThickness = min(minThickness, mainDist);

    // Early termination: zaten çok ince bulduk
    if (minThickness < 0.5) {
        thicknesses[idx] = minThickness;
        return;
    }

    // Koni içinde ek yönler (sadece ana yön yeterli değilse)
    if (uniforms.coneRayCount > 0u && minThickness > 2.0) {
        let tbn = computeTangentBasis(normal);
        let cosAngle = cos(uniforms.coneAngle);
        let sinAngle = sin(uniforms.coneAngle);

        for (var i = 0u; i < uniforms.coneRayCount; i++) {
            let phi = f32(i) / f32(uniforms.coneRayCount) * 6.28318;

            let localDir = vec3f(
                sinAngle * cos(phi),
                sinAngle * sin(phi),
                -cosAngle
            );

            let worldDir = normalize(tbn * localDir);
            let coneOrigin = pos + worldDir * uniforms.epsilon;
            let coneDist = castRay(coneOrigin, worldDir, idx);
            minThickness = min(minThickness, coneDist);

            // Early termination
            if (minThickness < 1.0) {
                break;
            }
        }
    }

    thicknesses[idx] = minThickness;
}
`;
    }

    /**
     * WebGL2 fallback (transform feedback kullanarak)
     */
    private async analyzeWebGL2(
        positions: Float32Array,
        normals: Float32Array,
        bvhMesh: THREE.Mesh,
        vertexCount: number,
        config: ThicknessAnalysisConfig
    ): Promise<Float32Array> {
        // WebGL2 için CPU-BVH hibrit yaklaşım
        return this.analyzeCPU(positions, normals, bvhMesh, vertexCount, config);
    }

    /**
     * CPU fallback (BVH accelerated)
     */
    private async analyzeCPU(
        positions: Float32Array,
        normals: Float32Array,
        bvhMesh: THREE.Mesh,
        vertexCount: number,
        config: ThicknessAnalysisConfig
    ): Promise<Float32Array> {
        const thicknesses = new Float32Array(vertexCount);
        const raycaster = this._raycaster;

        const batchSize = 500;
        let processed = 0;

        for (let i = 0; i < vertexCount; i++) {
            const pos = this._tempVec3_1.set(
                positions[i * 3],
                positions[i * 3 + 1],
                positions[i * 3 + 2]
            );

            const normal = this._tempVec3_2.set(
                normals[i * 3],
                normals[i * 3 + 1],
                normals[i * 3 + 2]
            ).normalize();

            let minDist = config.maxDistance;

            // Ana yön
            const inwardDir = this._tempVec3_3.copy(normal).negate();
            const origin = this._tempVec3_4.copy(pos).addScaledVector(inwardDir, config.epsilon);

            raycaster.set(origin, inwardDir);
            raycaster.far = config.maxDistance;

            const hits = raycaster.intersectObject(bvhMesh, false);
            for (const hit of hits) {
                if (hit.distance > config.epsilon) {
                    minDist = Math.min(minDist, hit.distance);
                    break;
                }
            }

            // Çoklu yön (koni)
            if (config.multiRayEnabled && config.coneRayCount > 0) {
                const coneAngleRad = (config.coneAngle * Math.PI) / 180;
                const cosAngle = Math.cos(coneAngleRad);
                const sinAngle = Math.sin(coneAngleRad);

                // Tangent hesapla
                let tangent = this._tempVec3_1;
                if (Math.abs(normal.x) < 0.9) {
                    tangent.set(1, 0, 0).cross(normal).normalize();
                } else {
                    tangent.set(0, 1, 0).cross(normal).normalize();
                }
                const bitangent = this._tempVec3_2.crossVectors(normal, tangent);

                for (let j = 0; j < config.coneRayCount; j++) {
                    const phi = (j / config.coneRayCount) * Math.PI * 2;

                    // Koni yönü hesapla
                    const coneDir = this._tempVec3_3;
                    coneDir.set(0, 0, 0);
                    coneDir.addScaledVector(tangent, sinAngle * Math.cos(phi));
                    coneDir.addScaledVector(bitangent, sinAngle * Math.sin(phi));
                    coneDir.addScaledVector(normal, -cosAngle);
                    coneDir.normalize();

                    const coneOrigin = this._tempVec3_4.copy(pos).addScaledVector(coneDir, config.epsilon);

                    raycaster.set(coneOrigin, coneDir);
                    const coneHits = raycaster.intersectObject(bvhMesh, false);

                    for (const hit of coneHits) {
                        if (hit.distance > config.epsilon) {
                            minDist = Math.min(minDist, hit.distance);
                            break;
                        }
                    }
                }
            }

            thicknesses[i] = minDist;

            processed++;
            if (processed % batchSize === 0) {
                config.onProgress?.(10 + (processed / vertexCount) * 80);
                await new Promise(resolve => setTimeout(resolve, 0));
            }
        }

        return thicknesses;
    }

    /**
     * SSS faktörlerini hesapla
     */
    private computeSSSFactors(
        thicknesses: Float32Array,
        config: ThicknessAnalysisConfig
    ): Float32Array {
        const sssFactors = new Float32Array(thicknesses.length);

        // Min/max bul
        let minT = Infinity;
        let maxT = 0;
        for (let i = 0; i < thicknesses.length; i++) {
            const t = thicknesses[i];
            if (t > 0 && t < config.maxDistance) {
                minT = Math.min(minT, t);
                maxT = Math.max(maxT, t);
            }
        }

        const range = maxT - minT;
        if (range <= 0) {
            return sssFactors;
        }

        // SSS: kalınlık azaldıkça artar (Beer-Lambert benzeri)
        const sssScale = 0.5;
        for (let i = 0; i < thicknesses.length; i++) {
            const t = thicknesses[i];
            if (t > 0 && t < config.maxDistance) {
                const normalized = (t - minT) / range;
                sssFactors[i] = Math.exp(-normalized * sssScale);
            }
        }

        return sssFactors;
    }

    /**
     * İstatistikleri hesapla
     */
    private computeStatistics(
        thicknesses: Float32Array,
        config: ThicknessAnalysisConfig
    ): ThicknessStatistics {
        const validValues: number[] = [];

        for (let i = 0; i < thicknesses.length; i++) {
            const t = thicknesses[i];
            if (t > 0 && t < config.maxDistance * 0.99) {
                validValues.push(t);
            }
        }

        if (validValues.length === 0) {
            return {
                minThickness: 0,
                maxThickness: 0,
                avgThickness: 0,
                medianThickness: 0,
                stdDeviation: 0,
                criticalPercentage: 0,
                validVertexCount: 0,
                totalVertexCount: thicknesses.length,
                histogram: []
            };
        }

        // Sırala
        validValues.sort((a, b) => a - b);

        const min = validValues[0];
        const max = validValues[validValues.length - 1];

        // Ortalama
        const sum = validValues.reduce((a, b) => a + b, 0);
        const avg = sum / validValues.length;

        // Medyan
        const midIdx = Math.floor(validValues.length / 2);
        const median = validValues.length % 2 === 0
            ? (validValues[midIdx - 1] + validValues[midIdx]) / 2
            : validValues[midIdx];

        // Standart sapma
        const sqDiffSum = validValues.reduce((acc, val) => acc + (val - avg) ** 2, 0);
        const stdDev = Math.sqrt(sqDiffSum / validValues.length);

        // Kritik yüzde
        const criticalCount = validValues.filter(v => v < config.criticalThickness).length;
        const criticalPercentage = (criticalCount / validValues.length) * 100;

        // Histogram - count değerleri 0-100 arası normalize edilir (görselleştirme için)
        const histogram: { bin: number; count: number }[] = [];
        const binWidth = (max - min) / config.histogramBins;

        // Önce ham sayıları topla
        const rawCounts: number[] = [];
        for (let i = 0; i < config.histogramBins; i++) {
            const binStart = min + i * binWidth;
            const binEnd = binStart + binWidth;
            const count = validValues.filter(v => v >= binStart && v < binEnd).length;
            rawCounts.push(count);
        }

        // Maksimum değeri bul ve normalize et (0-100 arası)
        const maxCount = Math.max(...rawCounts, 1);
        for (let i = 0; i < config.histogramBins; i++) {
            const binStart = min + i * binWidth;
            const normalizedCount = (rawCounts[i] / maxCount) * 100;
            histogram.push({
                bin: binStart + binWidth / 2,
                count: normalizedCount
            });
        }

        return {
            minThickness: min,
            maxThickness: max,
            avgThickness: avg,
            medianThickness: median,
            stdDeviation: stdDev,
            criticalPercentage,
            validVertexCount: validValues.length,
            totalVertexCount: thicknesses.length,
            histogram
        };
    }

    /**
     * GPU initialization'ın tamamlanmasını bekle
     */
    async waitForInit(): Promise<void> {
        if (this.gpuInitPromise) {
            await this.gpuInitPromise;
        }
    }

    /**
     * Mevcut GPU modunu al
     */
    getGPUMode(): GPUMode {
        return this.gpuMode;
    }

    /**
     * GPU device kayboldu mu?
     */
    isDeviceLost(): boolean {
        return this.gpuDeviceLost;
    }

    /**
     * GPU'yu yeniden başlatmayı dene
     */
    async tryRecoverGPU(): Promise<boolean> {
        if (!this.gpuDeviceLost) {
            return true;
        }

        this.gpuDeviceLost = false;
        this.gpuDevice = null;

        await this.initializeGPU();

        if (this.gpuMode === 'WebGPU' && this.gpuDevice) {
            return true;
        }

        return false;
    }

    /**
     * Konfigürasyonu güncelle
     */
    setConfig(config: Partial<ThicknessAnalysisConfig>): void {
        Object.assign(this.config, config);
    }

    /**
     * Kaynakları temizle
     */
    dispose(): void {
        this.cachedBVH = null;
        if (this.cachedMesh) {
            this.cachedMesh.geometry.dispose();
            (this.cachedMesh.material as THREE.Material).dispose();
            this.cachedMesh = null;
        }
    }
}

// Singleton instance
let analyzerInstance: FikoGPUThicknessAnalyzer | null = null;

/**
 * Singleton GPU analyzer al
 */
export function getGPUThicknessAnalyzer(): FikoGPUThicknessAnalyzer {
    if (!analyzerInstance) {
        analyzerInstance = new FikoGPUThicknessAnalyzer();
    }
    return analyzerInstance;
}
